import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import AppShell from "../components/AppShell";
import ApiService from "../services/ApiService";
import MetaTestService from "../services/MetaTestService";
import { isPlatformAdmin } from "../shared/utils/account-role";

const MIN_SUCCESS_RATE = 85;

function MetricCard({ label, value, color }) {
  return (
    <Paper
      sx={{
        flex: 1,
        p: 2,
        textAlign: "center",
        background: "#F8FAFC",
        border: "1px solid #E8EDF5",
        borderRadius: 2,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value}
      </Typography>
    </Paper>
  );
}

function StatusChip({ status }) {
  const map = {
    idle: { label: "Idle", color: "default" },
    running: { label: "Running", color: "info" },
    done: { label: "Done", color: "success" },
    stopped: { label: "Stopped", color: "warning" },
  };
  const { label, color } = map[status] || map.idle;
  return <Chip label={label} color={color} size="small" sx={{ fontWeight: 600, height: 22 }} />;
}

export default function MetaApiTestView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);

  // Connected Meta ad accounts fetched from server
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // Config (can be auto-filled or manually overridden)
  const [accessToken, setAccessToken] = useState("");
  const [adAccountId, setAdAccountId] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [totalCalls, setTotalCalls] = useState(520);
  const [delayMs, setDelayMs] = useState(100);

  // Run state
  const [status, setStatus] = useState("idle"); // idle | running | done | stopped
  const [counters, setCounters] = useState({ done: 0, success: 0, fail: 0, startedAt: null });
  const [log, setLog] = useState([]);
  const [snackbar, setSnackbar] = useState(null);

  const runningRef = useRef(false);
  const logEndRef = useRef(null);

  // Load account + connected Meta accounts on mount
  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const [meRes, metaRes] = await Promise.all([
        ApiService.getAccountMe().catch(() => null),
        MetaTestService.listMetaAccounts().catch(() => []),
      ]);
      const me = meRes?.account ?? null;
      if (!isPlatformAdmin(me)) {
        navigate("/", { replace: true });
        return;
      }
      setAccount(me);
      const accounts = Array.isArray(metaRes) ? metaRes : [];
      setMetaAccounts(accounts);
      // Auto-select first active account
      const first = accounts.find((a) => a.status === "active") || accounts[0];
      if (first) {
        setSelectedAccountId(first.id);
        setAccessToken(first.accessToken || "");
        setAdAccountId(first.adAccountId || "");
      }
    } catch {}
    finally { setLoadingAccounts(false); }
  }, [navigate]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // When user changes the dropdown, populate the fields
  function handleAccountSelect(id) {
    setSelectedAccountId(id);
    const found = metaAccounts.find((a) => a.id === id);
    if (found) {
      setAccessToken(found.accessToken || "");
      setAdAccountId(found.adAccountId || "");
    }
  }

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [log]);

  function appendLog(msg, ok = null) {
    setLog((prev) => [...prev.slice(-199), { msg, ok, ts: Date.now() }]);
  }

  function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  async function startCalls() {
    if (!accessToken.trim()) {
      setSnackbar({ severity: "warning", message: "Enter your Facebook access token." });
      return;
    }
    if (!adAccountId.trim()) {
      setSnackbar({ severity: "warning", message: "Enter your ad account ID (act_...)." });
      return;
    }

    runningRef.current = true;
    setStatus("running");
    setLog([]);

    try { await MetaTestService.reset(); } catch {}

    setCounters({ done: 0, success: 0, fail: 0, startedAt: new Date().toISOString() });
    appendLog(`▶ Starting ${totalCalls} calls — account: ${adAccountId.trim()} — delay: ${delayMs}ms`);

    for (let i = 1; i <= totalCalls; i++) {
      if (!runningRef.current) break;

      // Rotate through call types: me_accounts → campaigns → adsets → insights
      const callType = MetaTestService.getCallType(i - 1);

      try {
        const result = await MetaTestService.runCall(accessToken.trim(), adAccountId.trim(), callType);
        setCounters(result.counters || {});
        if (result.ok) {
          appendLog(`[${i}] ✓ 200 OK (${callType})`, true);
        } else {
          appendLog(`[${i}] ✗ ${result.graphError || `HTTP ${result.statusCode}`} (${callType})`, false);
        }
      } catch (err) {
        appendLog(`[${i}] ✗ ${err?.response?.data?.message || err.message || "Request failed"} (${callType})`, false);
        setCounters((prev) => ({
          ...prev,
          done: (prev.done || 0) + 1,
          fail: (prev.fail || 0) + 1,
        }));
      }

      if (i < totalCalls && runningRef.current) {
        await sleep(delayMs);
      }
    }

    const finalStatus = runningRef.current ? "done" : "stopped";
    runningRef.current = false;
    setStatus(finalStatus);
    appendLog(finalStatus === "done" ? "■ All calls complete." : "⏹ Stopped.");
  }

  function stopCalls() {
    runningRef.current = false;
    setStatus("stopped");
  }

  async function resetAll() {
    runningRef.current = false;
    setStatus("idle");
    setLog([]);
    setCounters({ done: 0, success: 0, fail: 0, startedAt: null });
    try { await MetaTestService.reset(); } catch {}
  }

  async function handleSignOut() {
    try { await ApiService.signout(); } catch {}
    navigate("/signin");
  }

  const { done = 0, success = 0, fail = 0 } = counters;
  const pct = Math.min(Math.round((done / totalCalls) * 100), 100);
  const rate = done > 0 ? Math.round((success / done) * 100) : null;
  const rateOk = rate !== null && rate >= MIN_SUCCESS_RATE;
  const eta = (() => {
    if (!counters.startedAt || done === 0 || status !== "running") return null;
    const elapsed = (Date.now() - new Date(counters.startedAt)) / 1000;
    const rem = Math.round((elapsed / done) * (totalCalls - done));
    return `~${rem}s remaining`;
  })();

  const isRunning = status === "running";
  const hasConnectedAccounts = metaAccounts.length > 0;

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Meta API test tool">
      <Stack spacing={3} sx={{ maxWidth: 820 }}>

        {/* Header */}
        <Box>
          <Typography variant="h2">Meta API test tool</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Run 500+ Marketing API calls to satisfy Meta App Review requirements (≥85% success rate).
          </Typography>
        </Box>

        {/* Auto-populate banner */}
        {!loadingAccounts && hasConnectedAccounts && (
          <Alert
            severity="success"
            icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
            sx={{ borderRadius: 2 }}
          >
            <strong>{metaAccounts.length} connected Meta ad account{metaAccounts.length > 1 ? "s" : ""} found.</strong>{" "}
            Token and account ID have been auto-filled. Select a different account below if needed.
          </Alert>
        )}

        {!loadingAccounts && !hasConnectedAccounts && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            No connected Meta ad accounts found. Go to{" "}
            <strong>Data sources</strong> and connect a Meta account first, or enter your token and account ID manually below.
          </Alert>
        )}

        {/* Settings card */}
        <Paper sx={{ p: 3, border: "1px solid #E8EDF5", borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
            <Typography variant="h5" sx={{ flex: 1 }}>Settings</Typography>
            <StatusChip status={status} />
          </Stack>

          <Stack spacing={2.5}>
            {/* Connected account selector */}
            {hasConnectedAccounts && (
              <FormControl fullWidth size="small" disabled={isRunning}>
                <InputLabel>Connected ad account</InputLabel>
                <Select
                  value={selectedAccountId}
                  label="Connected ad account"
                  onChange={(e) => handleAccountSelect(e.target.value)}
                  renderValue={(id) => {
                    const acc = metaAccounts.find((a) => a.id === id);
                    return acc ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CampaignOutlinedIcon sx={{ fontSize: 16, color: "#1877F2" }} />
                        <span>{acc.displayName}</span>
                        <Chip
                          label={acc.adAccountId}
                          size="small"
                          sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#EFF6FF", color: "#1877F2" }}
                        />
                      </Stack>
                    ) : id;
                  }}
                >
                  {metaAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                        <CampaignOutlinedIcon sx={{ fontSize: 18, color: "#1877F2" }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{acc.displayName}</Typography>
                          <Typography variant="caption" color="text.secondary">{acc.adAccountId}</Typography>
                        </Box>
                        <Chip
                          label={acc.status}
                          size="small"
                          color={acc.status === "active" ? "success" : "default"}
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Access token"
              type={showToken ? "text" : "password"}
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              disabled={isRunning}
              fullWidth
              size="small"
              placeholder="Auto-filled from connected account or paste manually"
              helperText={hasConnectedAccounts ? "Auto-filled from your connected Meta account" : "Paste your Facebook user access token"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowToken((v) => !v)} edge="end">
                      {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Ad account ID"
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              disabled={isRunning}
              fullWidth
              size="small"
              placeholder="act_123456789"
              helperText={hasConnectedAccounts ? "Auto-filled from your connected Meta account" : "Format: act_123456789"}
            />

            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Total calls</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{totalCalls}</Typography>
              </Stack>
              <Slider
                value={totalCalls}
                onChange={(_, v) => setTotalCalls(v)}
                min={500}
                max={600}
                step={10}
                disabled={isRunning}
                marks={[{ value: 500, label: "500" }, { value: 600, label: "600" }]}
                sx={{ mt: 1 }}
              />
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Delay between calls</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{delayMs}ms</Typography>
              </Stack>
              <Slider
                value={delayMs}
                onChange={(_, v) => setDelayMs(v)}
                min={50}
                max={500}
                step={50}
                disabled={isRunning}
                marks={[{ value: 50, label: "50ms" }, { value: 500, label: "500ms" }]}
                sx={{ mt: 1 }}
              />
            </Box>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <PlayArrowOutlinedIcon />}
              onClick={startCalls}
              disabled={isRunning || loadingAccounts}
              sx={{ flex: 1 }}
            >
              {isRunning ? "Running…" : "Start"}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<StopOutlinedIcon />}
              onClick={stopCalls}
              disabled={!isRunning}
              sx={{ flex: 1 }}
            >
              Stop
            </Button>
            <Tooltip title="Reset counters and log">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepOutlinedIcon />}
                  onClick={resetAll}
                  disabled={isRunning}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Metric cards */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <MetricCard label="Completed" value={done} />
          <MetricCard label="Successful" value={success} color="#10B981" />
          <MetricCard label="Failed" value={fail} color={fail > 0 ? "#EF4444" : undefined} />
          <MetricCard
            label="Success rate"
            value={rate !== null ? `${rate}%` : "—"}
            color={rate === null ? undefined : rateOk ? "#10B981" : "#EF4444"}
          />
        </Stack>

        {/* Progress */}
        <Paper sx={{ p: 2.5, border: "1px solid #E8EDF5", borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {done} / {totalCalls} calls
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {eta && <Typography variant="caption" color="text.secondary">{eta}</Typography>}
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{pct}%</Typography>
            </Stack>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={status === "done" && rateOk ? "success" : status === "done" && !rateOk ? "error" : "primary"}
            sx={{ height: 8, borderRadius: 4 }}
          />

          {status === "done" && (
            <Alert
              severity={rateOk ? "success" : "error"}
              icon={rateOk ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
              sx={{ mt: 2 }}
            >
              {rateOk
                ? `${success}/${done} calls succeeded (${rate}%). Go back to Meta App Review and save — it may take up to 24 hours to reflect.`
                : `Success rate ${rate}% is below the required 85%. Check your token / account ID and try again.`}
            </Alert>
          )}
        </Paper>

        {/* Live log */}
        <Paper sx={{ p: 2.5, border: "1px solid #E8EDF5", borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Live log</Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setLog([])}
              disabled={isRunning}
            >
              Clear
            </Button>
          </Stack>
          <Box
            sx={{
              fontFamily: "monospace",
              fontSize: "0.75rem",
              bgcolor: "#F8FAFC",
              border: "1px solid #E8EDF5",
              borderRadius: 1.5,
              p: 1.5,
              height: 200,
              overflowY: "auto",
              color: "text.secondary",
            }}
          >
            {log.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                Log will appear here once you start.
              </Typography>
            )}
            {log.map((entry, i) => (
              <Box
                key={i}
                sx={{
                  color: entry.ok === true ? "#10B981" : entry.ok === false ? "#EF4444" : "text.secondary",
                  lineHeight: 1.6,
                }}
              >
                {entry.msg}
              </Box>
            ))}
            <div ref={logEndRef} />
          </Box>
        </Paper>

      </Stack>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled" sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppShell>
  );
}
